import { useEffect } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import BundleEditDialogComponent from "./BundleEditDialog.component";
import {
  createBundle,
  getBundleCompanies,
  getModuleListing,
  updateBundle,
} from "../../redux/bundles.action";
import { selectLoading } from "../../../notification";

export default function BundleEditDialogContainer({ open, onClose, record, id, onEdit, canEdit }) {
  // Either write disables the whole footer — the two buttons are alternatives, not a queue.
  const isUpdating = useSelector((state) =>
    selectLoading(state, updateBundle.typePrefix));
  const isCreating = useSelector((state) =>
    selectLoading(state, createBundle.typePrefix));
  const moduleListing = useSelector((state) =>
    state.bundles.moduleListing);
  // The count is stamped with the bundle it was fetched for (see bundles.slice.js), so a
  // response that lands after the admin moved to another bundle is ignored rather than shown.
  const bundleCompanies = useSelector((state) =>
    state.bundles.bundleCompanies);
  const companyCount = bundleCompanies.bundleId === id ? bundleCompanies.total : 0;
  // The REPLACE confirmation gate depends on this count, so an in-place Save cannot be
  // allowed while it is unknown (#5). Three states, distinguishable from the slice + the
  // generic loading flag:
  //   * pending  — the fetch is in flight (loading true; pending() has already stamped id);
  //   * known    — it resolved for THIS bundle (loading false, stamp === id);
  //   * failed   — it was rejected (loading false, rejected() reset the stamp to null).
  const isCountLoading = useSelector((state) =>
    selectLoading(state, getBundleCompanies.typePrefix));
  const countPending = isCountLoading;
  const countFailed = !isCountLoading && bundleCompanies.bundleId !== id;
  const dispatch = useDispatch();

  // Re-run the count fetch after a failure, so the admin can get to a state where Save is
  // allowed again without reopening the dialog.
  const onRetryCount = () => {
    dispatch(getBundleCompanies({ id }));
  };

  useEffect(() => {
    // Authoring a bundle composes from the ACTIVE modules only. A module the bundle already
    // holds that has since been retired stays in the moduleIds it was seeded with (the grid
    // only toggles what it renders), so nothing is silently dropped on Save. The cache is
    // shared with the company/approval dialogs, which fill it with the full retired-included
    // set, so this always refetches active-only rather than trusting the shared cache.
    dispatch(getModuleListing({ activeOnly: true }));
  }, []);

  // Save: edit in place. Carries the id, so every company already on this bundle is
  // re-flattened against the new module set.
  const onSubmit = (value) => {
    dispatch(updateBundle({
      id,
      name: value.name,
      description: value.description,
      moduleIds: value.moduleIds,
    }))
      .then(unwrapResult)
      .then(() => {
        onClose();
        onEdit();
      })
      .catch(() => {});
  };

  // Save as new: the SAME payload the Add dialog sends, deliberately WITHOUT the id. The
  // original bundle and the companies on it are left exactly as they were.
  const onSubmitAsNew = (value) => {
    dispatch(createBundle({
      name: value.name,
      description: value.description,
      moduleIds: value.moduleIds,
    }))
      .then(unwrapResult)
      .then(() => {
        onClose();
        onEdit();
      })
      .catch(() => {});
  };

  return <BundleEditDialogComponent
    moduleListing={moduleListing}
    record={record}
    open={open}
    onClose={onClose}
    onSubmit={onSubmit}
    onSubmitAsNew={onSubmitAsNew}
    isLoading={isUpdating || isCreating}
    companyCount={companyCount}
    countPending={countPending}
    countFailed={countFailed}
    onRetryCount={onRetryCount}
    canEdit={canEdit}
  />;
}
